export interface IPaginatedList<T> {
  data: T[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface IDocument {
  id: number;
  documentId: string;
  title: string;
  url: string;
  url_custom: string;
  type: string;
  extract: string;
  content: string;
  uploaded_by: string;
  date_published: string;
  scheduling: string;
  alt_text: string;
  caption: string;
  document_status: string;
  visibility: string;
  file_name: string;
  file_type: FileType | null;
  file_size: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  meta_tags: string[];
  trending: boolean;
  entityType: 'article' | 'event' | 'document' | 'tool';
  categories: ICategory[];
  thumbnail_photo?: IPhoto;
}

export enum FileType {
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  XLS = 'xls',
  XLSX = 'xlsx',
  PPT = 'ppt',
  PPTX = 'pptx',
  OTHER = 'other',
}

export interface INews {
  id: number;
  documentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  url: string;
  content: string;
  extract: string;
  header_photo_position: string;
  thumbnail_photo: IPhoto;
  header_photo: IPhoto;
  author: string;
  date_published: string;
  scheduling: string;
  article_status: string;
  visibility: string;
  meta_tags: {};
  trending: boolean;
  entityType: 'article' | 'event' | 'document' | 'tool';
  categories: ICategory[];
}

export interface IEvent {
  participant_age: string;
  id: number;
  documentId: string;
  title: string;
  url: string;
  content: string;
  extract: string;
  header_photo_position: string;
  author: string;
  date_published: string;
  scheduling: string | null;
  event_status: string;
  visibility: string;
  event_date: string;
  event_end: string;
  duration: string;
  location: string;
  external_url: string;
  vacancy: number;
  price?: number;
  meta_tags: {};
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  applications_date: Date;
  thumbnail_photo: IPhoto;
  header_photo: IPhoto;
  trending: boolean;
  entityType: 'article' | 'event' | 'document' | 'tool';
  organizer: [
    {
      name: string;
      url?: string;
      id: number;
    },
  ];
  categories: ICategory[];
  assembly_documents?: IAssemblyDocument[];
}

export interface IAssemblyDocument {
  id: number;
  title: string;
  publication_date: string;
  status: string;
  file?: {
    id: number;
    documentId: string;
    name: string;
    alternativeText: string;
    caption: string;
    focalPoint: any;
    width: number | null;
    height: number | null;
    formats: any;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string | null;
    provider: string;
    provider_metadata: any;
    createdAt: string;
    updatedAt: string;
    publishedAt?: string;
    legacyMediaId: string;
    legacySourceUrl: string;
  };
}

export interface ITool {
  id: number;
  documentId: string;
  title: string;
  url: string;
  extract: string;
  uploaded_by: string;
  date_published: string;
  scheduling: string | null;
  alt_text: string;
  caption: string;
  tool_status: string;
  visibility: string;
  cip: string;
  meta_tags: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  trending: boolean;
  entityType: 'article' | 'event' | 'document' | 'tool';
  categories: ICategory[];
  thumbnail_photo?: IPhoto;
}

export interface IBanner {
  id: number;
  documentId: string;
  title: string;
  subtext: string;
  scheduling: string | null;
  banner_status: string;
  visibility: string;
  link: string;
  color: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  thumbnail_photo: {
    name: string;
    url: string;
  };
}

export type SearchResult = INews | IEvent | ITool | IDocument | IStaticPage;

export interface IStaticPageMetaTag {
  id: number;
  value: string;
}

export interface IStaticPage {
  id: number;
  documentId: string;
  locale: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  title: string;
  url: string;
  searchSnippet?: string;
  meta_tags: IStaticPageMetaTag[];
  entityType: 'static-page';
}

interface IPhoto {
  alternativeText?: string;
  formats?: {
    large?: {
      url?: string;
    };
    medium?: {
      url?: string;
    };
    small?: {
      url?: string;
    };
    thumbnail?: {
      url?: string;
    };
  };
}

export interface IUser {
  id: number;
  username: string;
  email: string;
  name: string;
  surname: string;
  date_of_birth: string;
  address: string;
  postal_code: string;
  city: string;
  phone: string;
  membership_paid: boolean;
  magazine_subscription: boolean;
  functions: string[];
  blocked: boolean;
  confirmed: boolean;
  member_number: string;
  role: {
    id: number;
    name: string;
    type: string;
  };
}

export interface IGridPosition {
  x: number;
  y: number;
  // Optional so configs saved before resizing existed still load fine -
  // grid-layout.ts falls back to the item's default cols/rows for those.
  cols?: number;
  rows?: number;
  // Whether the user has hidden this card from the dashboard editor. Optional
  // so configs saved before it existed still load (treated as shown).
  hidden?: boolean;
}

export interface IUserConfig {
  darkMode: boolean;
  displayType: 'card' | 'list';
  gridPosition: Record<string, IGridPosition>;
  extra?: Record<string, unknown>;
}

export type IFormStatus =
  | 'INITIAL'
  | 'SUBMITTED'
  | 'PENDING'
  | 'SUCCESS'
  | 'ERROR';

export type SearchType = 'news' | 'events' | 'documents' | 'tools' | 'global';

export const GLOBAL_CATEGORIES_KEY = 'globalCategories';
export const GLOBAL_SEARCH_KEY = 'globalSearch';
export const GLOBAL_DATE_FROM_KEY = 'globalDateFrom';
export const GLOBAL_DATE_TO_KEY = 'globalDateTo';
export const NEWS_DATE_FROM_KEY = 'newsDateFrom';
export const NEWS_DATE_TO_KEY = 'newsDateTo';
export const EVENTS_DATE_FROM_KEY = 'eventsDateFrom';
export const EVENTS_DATE_TO_KEY = 'eventsDateTo';
export const DOCUMENTS_DATE_FROM_KEY = 'documentsDateFrom';
export const DOCUMENTS_DATE_TO_KEY = 'documentsDateTo';
export const TOOLS_DATE_FROM_KEY = 'toolsDateFrom';
export const TOOLS_DATE_TO_KEY = 'toolsDateTo';
export const NEWS_CATEGORIES_KEY = 'newsCategories';
export const NEWS_SEARCH_KEY = 'newsSearch';
export const EVENTS_CATEGORIES_KEY = 'eventsCategories';
export const EVENTS_SEARCH_KEY = 'eventsSearch';
export const DOCUMENTS_CATEGORIES_KEY = 'documentsCategories';
export const DOCUMENTS_SEARCH_KEY = 'documentsSearch';
export const TOOLS_CATEGORIES_KEY = 'toolsCategories';
export const TOOLS_SEARCH_KEY = 'toolsSearch';
export const NEWS_SORT_KEY = 'newsSort';
export const EVENTS_SORT_KEY = 'eventsSort';
export const DOCUMENTS_SORT_KEY = 'documentsSort';
export const TOOLS_SORT_KEY = 'toolsSort';
export const DOCUMENTS_FILE_TYPE_KEY = 'documentsFileType';

export interface ICategory {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  level: number;
  order: number;
  legacyId: number | null;
  legacySource: string | null;
  legacyPath: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  parent: ICategory | null;
  children?: ICategory[];
}

export type SortDirection = 'asc' | 'desc';

export type ReservationRequest = {
  resources: string[];
  start_date: string;
  end_date: string;
  name: string;
  email: string;
  phone?: string;
  organization: string;
  message: string;
};

export interface Reservation {
  date: string;
  prikolica: boolean;
  kombi: boolean;
}

export const STORAGE_KEYS = [
  GLOBAL_CATEGORIES_KEY,
  NEWS_CATEGORIES_KEY,
  EVENTS_CATEGORIES_KEY,
  DOCUMENTS_CATEGORIES_KEY,
  TOOLS_CATEGORIES_KEY,
  DOCUMENTS_FILE_TYPE_KEY,
  'search',
  'sort',
  'date',
] as const;

export const CATEGORY_STORAGE_KEYS = [
  GLOBAL_CATEGORIES_KEY,
  NEWS_CATEGORIES_KEY,
  EVENTS_CATEGORIES_KEY,
  DOCUMENTS_CATEGORIES_KEY,
  TOOLS_CATEGORIES_KEY,
] as const;

export const SEARCH_STORAGE_KEYS = [
  GLOBAL_SEARCH_KEY,
  NEWS_SEARCH_KEY,
  EVENTS_SEARCH_KEY,
  DOCUMENTS_SEARCH_KEY,
  TOOLS_SEARCH_KEY,
  DOCUMENTS_FILE_TYPE_KEY,
] as const;

export const SORT_STORAGE_KEYS = [
  NEWS_SORT_KEY,
  EVENTS_SORT_KEY,
  DOCUMENTS_SORT_KEY,
  TOOLS_SORT_KEY,
] as const;

export const DATE_STORAGE_KEYS = [
  GLOBAL_DATE_FROM_KEY,
  GLOBAL_DATE_TO_KEY,
  NEWS_DATE_FROM_KEY,
  NEWS_DATE_TO_KEY,
  EVENTS_DATE_FROM_KEY,
  EVENTS_DATE_TO_KEY,
  DOCUMENTS_DATE_FROM_KEY,
  DOCUMENTS_DATE_TO_KEY,
  TOOLS_DATE_FROM_KEY,
  TOOLS_DATE_TO_KEY,
] as const;

export type CategoryStorageKey = (typeof CATEGORY_STORAGE_KEYS)[number];
export type SearchStorageKey = (typeof SEARCH_STORAGE_KEYS)[number];
export type SortStorageKey = (typeof SORT_STORAGE_KEYS)[number];
export type DateStorageKey = (typeof DATE_STORAGE_KEYS)[number];

// export type NewsSortType = 'title' | 'date_published' | 'category';

// export type EventsSortType = 'title' | 'event_date' | 'category';

// export type DocumentsSortType =
//   | 'title'
//   | 'date_published'
//   | 'category'
//   | 'file_type';

// export type ToolsSortType = 'title' | 'category';

// export type SortType =
//   | NewsSortType
//   | EventsSortType
//   | DocumentsSortType
//   | ToolsSortType;
